import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { API_URL } from '../../api';
import { useConfirm } from '../../components/ConfirmDialog';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const STATUS_COLOR = { available: 'success', booked: 'info', cancelled: 'default' };

const fmtDate = (s) => {
    const d = new Date(s);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtTime = (s) => {
    const d = new Date(s);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const dayKey = (s) => {
    const d = new Date(s);
    return d.toISOString().slice(0, 10);
};

const TIME_RE = /^\d{1,2}:\d{2}$/;

export default function ConsultationSlotsPage() {
    const confirm = useConfirm();
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const [singleOpen, setSingleOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/consultation-slots`, authHeaders());
            setSlots(data || []);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load slots.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const grouped = useMemo(() => {
        const map = new Map();
        for (const s of slots) {
            const k = dayKey(s.startsAt);
            if (!map.has(k)) map.set(k, []);
            map.get(k).push(s);
        }
        const groups = [...map.entries()].map(([k, items]) => ({
            key: k,
            date: new Date(items[0].startsAt),
            items: items.sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt)),
        }));
        groups.sort((a, b) => a.date - b.date);
        return groups;
    }, [slots]);

    const remove = async (id) => {
        if (!(await confirm({ title: 'Delete slot', message: 'Delete this consultation slot?', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(`${API_URL}/api/admin/consultation-slots/${id}`, authHeaders());
            setMessage('Slot deleted.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    const cancelBooking = async (id) => {
        if (!(await confirm({ title: 'Cancel booking', message: "The slot becomes available again. The parent isn't notified automatically.", confirmLabel: 'Cancel booking', danger: true }))) return;
        try {
            await axios.patch(`${API_URL}/api/admin/consultation-slots/${id}`, { status: 'available' }, authHeaders());
            setMessage('Booking cancelled — slot is available again.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">Consultations</Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={() => setSingleOpen(true)}
                        sx={{ padding: '8px 22px', fontFamily: '"Inter"', fontWeight: 500, color: '#fff !important' }}
                    >
                        Add slot
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setBulkOpen(true)}
                        sx={{ padding: '8px 22px', fontFamily: '"Inter"', fontWeight: 500 }}
                    >
                        Add many on a date
                    </Button>
                </Stack>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                    <Typography variant="caption" component="div" color="text.secondary">
                        <strong>1.</strong> A consultation slot is a bookable timeslot the public site offers on the Contact page.<br />
                        <strong>2.</strong> Click <strong>Add slot</strong> to add one — date, start time, duration, and how many people can book (usually 1).<br />
                        <strong>3.</strong> Parents book slots by picking a time on the public site — bookings appear on the slot detail page.<br />
                        <strong>4.</strong> Cancel a slot from its detail page — anyone who'd already booked is notified by email.<br />
                        <strong>5.</strong> Past slots stay in the list for record; use the filter to hide them.<br />
                    </Typography>
                </HowItWorksCard>
            </Box>

            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}>
                    <CircularProgress />
                </Stack>
            ) : grouped.length === 0 ? (
                <Card sx={{ p: 4 }}>
                    <Typography align="center" color="text.secondary">
                        No slots yet. Click "Add slot" or "Add many on a date" to publish availability.
                    </Typography>
                </Card>
            ) : (
                grouped.map((g) => (
                    <Card key={g.key} sx={{ p: 2, mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            {fmtDate(g.date)}
                        </Typography>
                        <Stack spacing={1}>
                            {g.items.map((s) => (
                                <Stack
                                    key={s._id}
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ flexWrap: 'wrap' }}
                                >
                                    <Box sx={{ fontFamily: 'monospace', minWidth: 80 }}>{fmtTime(s.startsAt)}</Box>
                                    <Box sx={{ color: 'text.secondary', minWidth: 60 }}>{s.durationMinutes || 15} min</Box>
                                    <Chip
                                        size="small"
                                        label={s.status}
                                        color={STATUS_COLOR[s.status] || 'default'}
                                    />
                                    {s.bookedByEnquiryId && (
                                        <Typography variant="body2">
                                            Booked by{' '}
                                            <RouterLink to={`/contact-enquiries/${s.bookedByEnquiryId._id || s.bookedByEnquiryId}`}>
                                                {s.bookedByEnquiryId.firstName} {s.bookedByEnquiryId.surname}
                                            </RouterLink>{' '}
                                            ({s.bookedByEnquiryId.email})
                                        </Typography>
                                    )}
                                    <Box sx={{ flex: 1 }} />
                                    {s.status === 'booked' && (
                                        <Button size="small" onClick={() => cancelBooking(s._id)}>
                                            Cancel booking
                                        </Button>
                                    )}
                                    {s.status !== 'booked' && (
                                        <IconButton onClick={() => remove(s._id)} size="small">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Stack>
                            ))}
                        </Stack>
                    </Card>
                ))
            )}

            <SingleSlotDialog
                open={singleOpen}
                onClose={() => setSingleOpen(false)}
                onCreated={async () => { setSingleOpen(false); setMessage('Slot created.'); await load(); }}
                onError={(m) => setError(m)}
            />
            <BulkSlotDialog
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                onCreated={async (n) => { setBulkOpen(false); setMessage(`${n} slot(s) created.`); await load(); }}
                onError={(m) => setError(m)}
            />
        </Container>
    );
}

function SingleSlotDialog({ open, onClose, onCreated, onError }) {
    const [datetime, setDatetime] = useState('');
    const [duration, setDuration] = useState(15);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!datetime) { onError('Pick a date and time.'); return; }
        setBusy(true);
        try {
            await axios.post(
                `${API_URL}/api/admin/consultation-slots`,
                { startsAt: new Date(datetime).toISOString(), durationMinutes: Number(duration) || 15 },
                authHeaders()
            );
            setDatetime('');
            setDuration(15);
            await onCreated();
        } catch (err) {
            onError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
            <DialogTitle>Add a single slot</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        type="datetime-local"
                        label="Date & time"
                        InputLabelProps={{ shrink: true }}
                        value={datetime}
                        onChange={(e) => setDatetime(e.target.value)}
                        fullWidth
                    />
                    <TextField
                        type="number"
                        label="Duration (minutes)"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        inputProps={{ min: 5, max: 180, step: 5 }}
                        fullWidth
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button onClick={submit} variant="contained" disabled={busy} sx={{ color: '#fff !important' }}>
                    {busy ? 'Adding…' : 'Add slot'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function BulkSlotDialog({ open, onClose, onCreated, onError }) {
    const [date, setDate] = useState('');
    const [start, setStart] = useState('14:00');
    const [end, setEnd] = useState('16:00');
    const [duration, setDuration] = useState(15);
    const [busy, setBusy] = useState(false);

    const previewTimes = useMemo(() => {
        if (!TIME_RE.test(start) || !TIME_RE.test(end)) return [];
        const [sh, sm] = start.split(':').map((n) => parseInt(n, 10));
        const [eh, em] = end.split(':').map((n) => parseInt(n, 10));
        const startMin = sh * 60 + sm;
        const endMin = eh * 60 + em;
        const step = Math.max(5, Math.min(180, Number(duration) || 15));
        const out = [];
        for (let m = startMin; m + step <= endMin; m += step) {
            out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
        }
        return out;
    }, [start, end, duration]);

    const submit = async () => {
        if (!date) { onError('Pick a date.'); return; }
        if (!previewTimes.length) { onError('That start/end/duration combination produces no slots.'); return; }
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/consultation-slots`,
                { date, times: previewTimes, durationMinutes: Number(duration) || 15 },
                authHeaders()
            );
            await onCreated(data?.created || previewTimes.length);
        } catch (err) {
            onError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>Add many slots on one day</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        type="date"
                        label="Date"
                        InputLabelProps={{ shrink: true }}
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        fullWidth
                    />
                    <Stack direction="row" spacing={2}>
                        <TextField
                            type="time"
                            label="From"
                            InputLabelProps={{ shrink: true }}
                            value={start}
                            onChange={(e) => setStart(e.target.value)}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            type="time"
                            label="To"
                            InputLabelProps={{ shrink: true }}
                            value={end}
                            onChange={(e) => setEnd(e.target.value)}
                            sx={{ flex: 1 }}
                        />
                    </Stack>
                    <TextField
                        type="number"
                        label="Each slot is (minutes)"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        inputProps={{ min: 5, max: 180, step: 5 }}
                        fullWidth
                    />
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Will create {previewTimes.length} slot(s):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {previewTimes.slice(0, 24).map((t) => (
                                <Chip key={t} label={t} size="small" />
                            ))}
                            {previewTimes.length > 24 && <Chip size="small" label={`+${previewTimes.length - 24} more`} />}
                        </Box>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button onClick={submit} variant="contained" disabled={busy || !previewTimes.length} sx={{ color: '#fff !important' }}>
                    {busy ? 'Creating…' : `Create ${previewTimes.length} slot(s)`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}